import React, { useEffect, useState } from "react";
import { Table, Modal, Badge, Card } from "react-bootstrap";
import axios from "axios";
import { FaMapMarkerAlt, FaCubes } from "react-icons/fa";

function BasicExample() {
  const [sortedClientiData, setSortedClientiData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "valore",
    direction: "descending",
  });

  const [showModal, setShowModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);

  /* =========================================================
     TIPO DOCUMENTO / ORIGINE

     OW4  => WEB
     OWU4 => WEB
     OU4  => NOWEB

     Regola:
     se il Tipo Documento contiene "W" => WEB
     altrimenti => NOWEB
     ========================================================= */

  const getTipoDocumento = (item) =>
    String(
      item["Tipo Documento"] ??
        item.tipo_documento ??
        item.tipo_doc ??
        item.tipo_ordine ??
        "",
    )
      .trim()
      .toUpperCase();

  const getOrigineOrdine = (item) => {
    const tipoDocumento = getTipoDocumento(item);

    if (tipoDocumento === "WEB") return "web";

    if (
      tipoDocumento === "NOWEB" ||
      tipoDocumento === "NO WEB" ||
      tipoDocumento === "NO-WEB"
    ) {
      return "noweb";
    }

    return tipoDocumento.includes("W") ? "web" : "noweb";
  };

  /* =========================================================
     FETCH
     ========================================================= */

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("mancata_vendita_dashboard.json");

        const clientiData = res.data?.TP?.localita || [];

        const transformedData = clientiData.reduce((acc, item) => {
          const id = item.id_cliente;

          const tipoDocumento = getTipoDocumento(item);
          const origine = getOrigineOrdine(item);

          const key = `${id}-${origine}`;

          const parseVal = (v) =>
            parseFloat(String(v ?? "0").replace(",", ".")) || 0;

          const valoreTotale = parseVal(item.Valore);

          const valoreLastMinute = parseVal(item.Valore_Last_Minute);

          const valoreNormale = parseVal(item.Valore_Normale);

          if (!acc[key]) {
            acc[key] = {
              id_cliente: id,
              cliente: item.clienti,
              localita: item.regione || item.nazione || item.paese || "—",
              origine,
              tipiDocumento: [],
              valore: 0,
              express: 0,
              regular: 0,
              numero: 0,
              dettagli: {},
            };
          }

          acc[key].valore += valoreTotale;
          acc[key].express += valoreLastMinute;
          acc[key].regular += valoreNormale;

          acc[key].numero += Number(item.numero_doc || 0);

          if (
            tipoDocumento &&
            !acc[key].tipiDocumento.includes(tipoDocumento)
          ) {
            acc[key].tipiDocumento.push(tipoDocumento);
          }

          const marchio = item.descrizione || "Generico";

          if (!acc[key].dettagli[marchio]) {
            acc[key].dettagli[marchio] = {
              valore: 0,
              note: [],
            };
          }

          acc[key].dettagli[marchio].valore += valoreTotale;

          if (item.note_agg) {
            acc[key].dettagli[marchio].note.push(item.note_agg);
          }

          return acc;
        }, {});

        Object.values(transformedData).forEach((cliente) => {
          cliente.tipoDocumento =
            cliente.tipiDocumento.length > 0
              ? cliente.tipiDocumento.join(" / ")
              : "—";

          cliente.dettagli = Object.entries(cliente.dettagli)
            .map(([marchio, info]) => ({
              descrizione: marchio,
              valore: info.valore,
              note: info.note.length > 0 ? info.note.join(" | ") : null,
            }))
            .sort((a, b) => b.valore - a.valore);
        });

        setSortedClientiData(
          sortArray(
            Object.values(transformedData),
            sortConfig.key,
            sortConfig.direction,
          ),
        );
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [sortConfig]);

  /* =========================================================
     SORT
     ========================================================= */

  const sortArray = (array, key, direction) =>
    [...array].sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;

      if (typeof av === "number") {
        return direction === "ascending" ? av - bv : bv - av;
      }

      return direction === "ascending"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

  const handleSort = (key) =>
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    });

  /* =========================================================
     FORMAT
     ========================================================= */

  const formatToEuro = (amount) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) {
      return null;
    }

    return sortConfig.direction === "ascending" ? " ↑" : " ↓";
  };

  /* =========================================================
     OPEN MODAL
     ========================================================= */

  const openCliente = (item) => {
    setSelectedCliente(item);
    setShowModal(true);
  };

  return (
    <>
      {/* =====================================================
          TABELLA CLIENTI
          ===================================================== */}

      <Card
        className="
          card
          ombra
          ordini-ora
          card-home
          hover-effect
          border-0
          tp-data-card
          tp-clients-card
        "
        style={{
          borderRadius: "15px",
          padding: "1.2rem",
          paddingBottom: "1.2rem",
        }}
      >
        <div
          className="
            no-scrollbar
            tp-table-scroll
            tp-client-table-scroll
          "
          style={{
            width: "100%",
            position: "relative",
          }}
        >
          <Table
            hover
            className="mb-0 custom-table tp-client-table"
            style={{
              fontSize: "0.8rem",
              width: "100%",
              margin: 0,
              borderCollapse: "separate",
            }}
          >
            <thead className="sticky-top tp-table-head" style={{ zIndex: 10 }}>
              <tr className="small">
                {/* CLIENTE */}
                <th
                  className="
                    ps-3
                    py-3
                    border-0
                    tp-col-cliente
                  "
                >
                  CLIENTE
                </th>

                {/* LOCALITÀ - NASCOSTA MOBILE */}
                <th
                  className="
                    py-3
                    border-0
                    cursor-pointer
                    tp-hide-mobile
                    tp-col-localita
                  "
                  onClick={() => handleSort("localita")}
                >
                  LOCALITÀ
                  <SortIcon colKey="localita" />
                </th>

                {/* VALORE */}
                <th
                  className="
                    py-3
                    border-0
                    text-end
                    cursor-pointer
                    tp-col-valore
                  "
                  onClick={() => handleSort("valore")}
                >
                  VALORE
                  <SortIcon colKey="valore" />
                </th>

                {/* LAST MINUTE - NASCOSTA MOBILE */}
                <th
                  className="
                    py-3
                    border-0
                    text-end
                    text-danger
                    cursor-pointer
                    tp-hide-mobile
                  "
                  onClick={() => handleSort("express")}
                >
                  LAST MINUTE
                  <SortIcon colKey="express" />
                </th>

                {/* REGULAR - NASCOSTA MOBILE */}
                <th
                  className="
                    py-3
                    border-0
                    text-end
                    text-secondary
                    cursor-pointer
                    tp-hide-mobile
                  "
                  onClick={() => handleSort("regular")}
                >
                  REGULAR
                  <SortIcon colKey="regular" />
                </th>

                {/* ORDINI */}
                <th
                  className="
                    py-3
                    border-0
                    text-center
                    tp-col-ordini
                  "
                >
                  ORDINI
                </th>

                {/* ORIGINE */}
                <th
                  className="
                    py-3
                    border-0
                    text-center
                    pe-3
                    tp-col-origine
                  "
                >
                  ORIGINE
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedClientiData.map((item, index) => (
                <tr
                  key={`${item.id_cliente}-${item.origine}-${index}`}
                  onClick={() => openCliente(item)}
                  className="
                      cursor-pointer
                      align-middle
                    "
                >
                  {/* CLIENTE */}
                  <td
                    className="
                        ps-3
                        fw-bold
                        truncate-cell
                        tp-col-cliente
                      "
                  >
                    {item.cliente}
                  </td>

                  {/* LOCALITÀ */}
                  <td
                    className="
                        truncate-cell
                        tp-hide-mobile
                      "
                    style={{
                      fontSize: "0.75rem",
                    }}
                  >
                    {item.localita}
                  </td>

                  {/* VALORE */}
                  <td
                    className="
                        text-end
                        fw-bold
                        no-wrap
                        tp-col-valore
                      "
                  >
                    {formatToEuro(item.valore)}
                  </td>

                  {/* LAST MINUTE */}
                  <td
                    className="
                        text-end
                        text-danger
                        fw-bold
                        no-wrap
                        tp-hide-mobile
                      "
                  >
                    {formatToEuro(item.express)}
                  </td>

                  {/* REGULAR */}
                  <td
                    className="
                        text-end
                        text-secondary
                        no-wrap
                        tp-hide-mobile
                      "
                    style={{
                      fontSize: "0.75rem",
                    }}
                  >
                    {formatToEuro(item.regular)}
                  </td>

                  {/* ORDINI */}
                  <td
                    className="
                        text-center
                        tp-col-ordini
                      "
                  >
                    <span>{item.numero}</span>
                  </td>

                  {/* ORIGINE */}
                 <td
  className="
    text-center
    pe-3
    tp-col-origine
  "
>
  <Badge pill className="tp-origin-badge">
    {String(item.origine || "noweb").trim().toLowerCase() === "web"
      ? "WEB"
      : "NOWEB"}
  </Badge>
</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* =====================================================
          MODALE CLIENTE LIQUID GLASS
          ===================================================== */}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
        dialogClassName="tp-client-modal-dialog"
        contentClassName="tp-client-modal-content"
        backdropClassName="tp-client-modal-backdrop"
      >
        {/* HEADER */}

        <Modal.Header
          closeButton
          className="
            tp-client-modal-header
            border-0
          "
        >
          <Modal.Title className="w-100">
            <div className="tp-client-modal-title">
              <div className="tp-client-modal-icon">
                <FaCubes size={20} />
              </div>

              <div className="tp-client-modal-heading">
                <div className="tp-client-modal-name">
                  {selectedCliente?.cliente}
                </div>

                <div className="tp-client-modal-location">
                  <FaMapMarkerAlt />

                  <span>{selectedCliente?.localita}</span>
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        {/* BODY */}

        <Modal.Body className="tp-client-modal-body">
          {/* BADGE INFO */}

          <div className="tp-client-modal-badges">
            <span className="tp-modal-pill">
              ID {selectedCliente?.id_cliente}
            </span>

            <span className="tp-modal-pill tp-modal-document">
              DOC {selectedCliente?.tipoDocumento || "—"}
            </span>

            <span className="tp-modal-pill tp-modal-lm">
              LM {formatToEuro(selectedCliente?.express)}
            </span>

            <span className="tp-modal-pill">
              REG {formatToEuro(selectedCliente?.regular)}
            </span>
          </div>

          {/* KPI TOTALE */}

          <div className="tp-client-modal-total">
            <div>
              <span>VALORE CLIENTE</span>

              <strong>{formatToEuro(selectedCliente?.valore)}</strong>
            </div>

            <div>
              <span>ORDINI</span>

              <strong>{selectedCliente?.numero || 0}</strong>
            </div>
          </div>

          {/* DETTAGLIO BRAND */}

          <div className="tp-client-detail-box">
            <div className="tp-client-detail-heading">
              <div>
                <span>DETTAGLIO ORDINI</span>
                <strong>Performance per brand</strong>
              </div>

              <span>{selectedCliente?.dettagli?.length} brand</span>
            </div>

            <div className="tp-client-detail-scroll">
              <Table
                className="
                  mb-0
                  tp-modal-detail-table
                "
              >
                <thead>
                  <tr>
                    <th>BRAND / NOTA</th>

                    <th className="text-end">VALORE</th>
                  </tr>
                </thead>

                <tbody>
                  {selectedCliente?.dettagli?.map((d, i) => (
                    <tr key={i}>
                      <td>
                        <div className="tp-modal-brand">
                          <strong>{d.descrizione}</strong>

                          {d.note && <small>{d.note}</small>}
                        </div>
                      </td>

                      <td className="text-end">
                        <strong className="tp-modal-value">
                          {formatToEuro(d.valore)}
                        </strong>
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr>
                    <td>TOTALE</td>

                    <td className="text-end">
                      {formatToEuro(selectedCliente?.valore)}
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* =====================================================
          CSS LOCALE COMPONENTE
          ===================================================== */}

      <style>{`
        /* ===============================================
           TABELLA
           =============================================== */

        .tp-client-table {
          width: 100% !important;
          margin: 0 !important;
        }

        .tp-client-table .truncate-cell {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .tp-client-table .no-wrap {
          white-space: nowrap;
        }

        .tp-client-table .cursor-pointer {
          cursor: pointer;
        }

        .tp-client-table tbody tr {
          transition:
            background 0.18s ease,
            transform 0.18s ease;
        }

        .tp-client-table tbody tr:hover td {
          background:
            rgba(
              255,
              255,
              255,
              0.035
            ) !important;
        }

        .tp-origin-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;

          min-width: 38px;

          padding: 4px 7px;

           color:
            #ff9a60 !important;

          background:
            linear-gradient(
              135deg,
              rgba(241, 111, 36, 0.20),
              rgba(241, 111, 36, 0.08)
            ) !important;

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.1
            );

          font-size: 0.58rem;
          font-weight: 800;
        }

        .tp-origin-badge.is-web {
          color: #ff7379 !important;

          background:
            rgba(
              184,
              31,
              37,
              0.15
            ) !important;

          border-color:
            rgba(
              224,
              55,
              63,
              0.28
            );
        }

        /* ===============================================
           BACKDROP
           =============================================== */

        .tp-client-modal-backdrop.show {
          opacity: 1 !important;

          background:
            rgba(
              5,
              7,
              10,
              0.7
            ) !important;

          backdrop-filter:
            blur(8px);

          -webkit-backdrop-filter:
            blur(8px);
        }

        /* ===============================================
           MODAL DIALOG
           =============================================== */

        .tp-client-modal-dialog {
          width:
            min(
              820px,
              calc(100vw - 32px)
            ) !important;

          max-width:
            820px !important;

          margin:
            1.5rem auto !important;
        }

        .tp-client-modal-content {
          position: relative;

          overflow: hidden;

          color: #f5f6f8 !important;

          background:
            radial-gradient(
              circle
              at 0% 0%,
              rgba(
                184,
                31,
                37,
                0.18
              ),
              transparent
              35%
            ),
            linear-gradient(
              145deg,
              rgba(
                39,
                42,
                49,
                0.92
              ),
              rgba(
                20,
                23,
                28,
                0.96
              )
            ) !important;

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.12
            ) !important;

          border-radius:
            24px !important;

          box-shadow:
            0 30px 80px
              rgba(
                0,
                0,
                0,
                0.52
              ),
            inset
              0 1px 0
              rgba(
                255,
                255,
                255,
                0.09
              ) !important;

          backdrop-filter:
            blur(28px)
            saturate(135%);

          -webkit-backdrop-filter:
            blur(28px)
            saturate(135%);
        }

        .tp-client-modal-content::before {
          content: "";

          position: absolute;

          top: 0;
          left: 22px;

          width: 38%;
          height: 1px;

          background:
            linear-gradient(
              90deg,
              #d72b33,
              transparent
            );

          pointer-events: none;
        }

        /* ===============================================
           CLOSE
           =============================================== */

        .tp-client-modal-content
        .btn-close {
          width: 32px;
          height: 32px;

          padding: 0 !important;

          border-radius: 50%;

          background-color:
            rgba(
              255,
              255,
              255,
              0.08
            );

          filter:
            invert(1)
            grayscale(1);

          opacity: 0.72;

          background-size: 11px;

          transition:
            opacity 0.18s ease,
            background-color
              0.18s ease;
        }

        .tp-client-modal-content
        .btn-close:hover {
          opacity: 1;

          background-color:
            rgba(
              184,
              31,
              37,
              0.18
            );
        }

        /* ===============================================
           HEADER
           =============================================== */

        .tp-client-modal-header {
          padding:
            22px
            22px
            12px !important;
        }

        .tp-client-modal-title {
          display: flex;
          align-items: center;

          min-width: 0;

          gap: 12px;
        }

        .tp-client-modal-icon {
          flex: 0 0 44px;

          width: 44px;
          height: 44px;

          display: flex;
          align-items: center;
          justify-content: center;

          color: #ff6269;

          background:
            linear-gradient(
              145deg,
              rgba(
                184,
                31,
                37,
                0.23
              ),
              rgba(
                184,
                31,
                37,
                0.08
              )
            );

          border:
            1px solid
            rgba(
              226,
              57,
              65,
              0.3
            );

          border-radius: 14px;

          box-shadow:
            inset
              0 1px 0
              rgba(
                255,
                255,
                255,
                0.06
              );
        }

        .tp-client-modal-heading {
          min-width: 0;
        }

        .tp-client-modal-name {
          max-width: 100%;

          overflow: hidden;

          color: #fff;

          font-size: 1.15rem;
          font-weight: 850;
          line-height: 1.2;

          text-overflow:
            ellipsis;

          white-space:
            nowrap;
        }

        .tp-client-modal-location {
          display: flex;
          align-items: center;

          gap: 5px;

          margin-top: 5px;

          color:
            rgba(
              255,
              255,
              255,
              0.52
            );

          font-size: 0.72rem;
          font-weight: 650;
        }

        .tp-client-modal-location svg {
          color: #d72b33;
        }

        /* ===============================================
           BODY
           =============================================== */

        .tp-client-modal-body {
          padding:
            8px
            22px
            22px !important;
        }

        /* ===============================================
           PILLS
           =============================================== */

        .tp-client-modal-badges {
          display: flex;
          flex-wrap: wrap;

          gap: 7px;

          margin-bottom: 14px;
        }

        .tp-modal-pill {
          display: inline-flex;
          align-items: center;

          min-height: 25px;

          padding:
            4px
            9px;

          color:
            rgba(
              255,
              255,
              255,
              0.65
            );

          background:
            rgba(
              255,
              255,
              255,
              0.055
            );

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.085
            );

          border-radius:
            999px;

          font-size: 0.61rem;
          font-weight: 800;
          letter-spacing:
            0.035em;
        }

        .tp-modal-origin.is-web,
        .tp-modal-lm {
          color: #ff6269;

          background:
            rgba(
              184,
              31,
              37,
              0.12
            );

          border-color:
            rgba(
              224,
              55,
              63,
              0.22
            );
        }

        /* ===============================================
           KPI
           =============================================== */

        .tp-client-modal-total {
          display: grid;

          grid-template-columns:
            1fr 0.4fr;

          gap: 10px;

          margin-bottom: 14px;
        }

        .tp-client-modal-total
        > div {
          padding:
            12px 14px;

          background:
            rgba(
              255,
              255,
              255,
              0.045
            );

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.075
            );

          border-radius: 14px;

          box-shadow:
            inset
              0 1px 0
              rgba(
                255,
                255,
                255,
                0.035
              );
        }

        .tp-client-modal-total span {
          display: block;

          margin-bottom: 4px;

          color:
            rgba(
              255,
              255,
              255,
              0.38
            );

          font-size: 0.57rem;
          font-weight: 850;
          letter-spacing:
            0.08em;
        }

        .tp-client-modal-total strong {
          display: block;

          color: #fff;

          font-size: 1.02rem;
          font-weight: 850;
        }

        /* ===============================================
           DETAIL BOX
           =============================================== */

        .tp-client-detail-box {
          overflow: hidden;

          background:
            rgba(
              10,
              12,
              16,
              0.26
            );

          border:
            1px solid
            rgba(
              255,
              255,
              255,
              0.075
            );

          border-radius: 16px;
        }

        .tp-client-detail-heading {
          display: flex;
          align-items: center;
          justify-content:
            space-between;

          gap: 12px;

          padding:
            12px 14px;

          border-bottom:
            1px solid
            rgba(
              255,
              255,
              255,
              0.07
            );
        }

        .tp-client-detail-heading
        > div {
          display: flex;
          flex-direction: column;
        }

        .tp-client-detail-heading
        div span {
          color:
            rgba(
              255,
              255,
              255,
              0.34
            );

          font-size: 0.55rem;
          font-weight: 850;
          letter-spacing:
            0.08em;
        }

        .tp-client-detail-heading
        div strong {
          margin-top: 2px;

          color:
            rgba(
              255,
              255,
              255,
              0.86
            );

          font-size: 0.78rem;
        }

        .tp-client-detail-heading
        > span {
          color:
            rgba(
              255,
              255,
              255,
              0.42
            );

          font-size: 0.65rem;
          font-weight: 700;
        }

        .tp-client-detail-scroll {
          max-height: 350px;

          overflow-y: auto;

          scrollbar-width: thin;

          scrollbar-color:
            rgba(
              184,
              31,
              37,
              0.55
            )
            transparent;
        }

        .tp-client-detail-scroll::-webkit-scrollbar {
          width: 5px;
        }

        .tp-client-detail-scroll::-webkit-scrollbar-thumb {
          background:
            rgba(
              184,
              31,
              37,
              0.55
            );

          border-radius:
            999px;
        }

        /* ===============================================
           MODAL TABLE
           =============================================== */

        .tp-modal-detail-table {
          width: 100%;

          color:
            rgba(
              255,
              255,
              255,
              0.72
            ) !important;

          margin: 0;
        }

        .tp-modal-detail-table
        thead th {
          position: sticky;

          top: 0;

          z-index: 2;

          padding:
            10px
            14px !important;

          color:
            rgba(
              255,
              255,
              255,
              0.37
            ) !important;

          background:
            rgba(
              18,
              20,
              25,
              0.94
            ) !important;

          border: 0 !important;

          font-size: 0.58rem;
          font-weight: 850;
          letter-spacing:
            0.07em;
        }

        .tp-modal-detail-table
        tbody td {
          padding:
            10px
            14px !important;

          color:
            rgba(
              255,
              255,
              255,
              0.72
            ) !important;

          background:
            transparent !important;

          border: 0 !important;
          border-bottom:
            1px solid
            rgba(
              255,
              255,
              255,
              0.055
            ) !important;
        }

        .tp-modal-detail-table
        tbody tr:hover td {
          background:
            rgba(
              255,
              255,
              255,
              0.035
            ) !important;
        }

        .tp-modal-brand {
          display: flex;
          flex-direction: column;

          min-width: 0;
        }

        .tp-modal-brand strong {
          color:
            rgba(
              255,
              255,
              255,
              0.88
            );

          font-size: 0.76rem;
        }

        .tp-modal-brand small {
          margin-top: 2px;

          color:
            rgba(
              255,
              255,
              255,
              0.36
            );

          font-size: 0.63rem;
        }

        .tp-modal-value {
          color: #ff656b;
        }

        .tp-modal-detail-table
        tfoot td {
          padding:
            12px
            14px !important;

          color: #fff !important;

          background:
            rgba(
              184,
              31,
              37,
              0.10
            ) !important;

          border: 0 !important;

          font-size: 0.78rem;
          font-weight: 850;
        }

        /* ===============================================
           MOBILE
           Mostra:
           CLIENTE / VALORE / ORDINI / ORIGINE
           =============================================== */

        @media
        screen and
        (max-width: 767px) {

          .tp-hide-mobile {
            display:
              none !important;
          }

          .tp-client-table {
            table-layout:
              fixed !important;
          }

          .tp-client-table
          .tp-col-cliente {
            width:
              40% !important;
          }

          .tp-client-table
          .tp-col-valore {
            width:
              28% !important;
          }

          .tp-client-table
          .tp-col-ordini {
            width:
              13% !important;
          }

          .tp-client-table
          .tp-col-origine {
            width:
              19% !important;
          }

          .tp-client-table
          thead th {
            padding:
              9px
              5px !important;

            font-size:
              0.53rem !important;
          }

          .tp-client-table
          tbody td {
            padding:
              10px
              5px !important;

            font-size:
              0.65rem !important;
          }

          .tp-client-table
          .tp-col-cliente {
            padding-left:
              8px !important;
          }

          .tp-client-table
          .tp-col-origine {
            padding-right:
              6px !important;
          }

          .tp-origin-badge {
            min-width: 32px;

            padding:
              3px
              5px;

            font-size:
              0.51rem;
          }

          /* MODAL MOBILE */

          .tp-client-modal-dialog {
            width:
              calc(
                100vw - 16px
              ) !important;

            max-width:
              none !important;

            margin:
              8px auto !important;
          }

          .tp-client-modal-content {
            max-height:
              calc(
                100dvh - 16px
              );

            border-radius:
              20px !important;
          }

          .tp-client-modal-header {
            padding:
              17px
              16px
              10px !important;
          }

          .tp-client-modal-body {
            padding:
              7px
              16px
              16px !important;

            overflow-y: auto;
          }

          .tp-client-modal-icon {
            flex-basis: 38px;

            width: 38px;
            height: 38px;

            border-radius: 12px;
          }

          .tp-client-modal-name {
            font-size: 1rem;
          }

          .tp-client-modal-badges {
            gap: 5px;

            margin-bottom:
              10px;
          }

          .tp-modal-pill {
            min-height: 23px;

            padding:
              3px
              7px;

            font-size: 0.55rem;
          }

          .tp-client-modal-total {
            grid-template-columns:
              1fr 0.45fr;

            gap: 7px;

            margin-bottom:
              10px;
          }

          .tp-client-modal-total
          > div {
            padding:
              10px
              11px;
          }

          .tp-client-modal-total
          strong {
            font-size:
              0.9rem;
          }

          .tp-client-detail-scroll {
            max-height:
              42vh;
          }

          .tp-modal-detail-table
          thead th,
          .tp-modal-detail-table
          tbody td,
          .tp-modal-detail-table
          tfoot td {
            padding:
              9px
              10px !important;
          }
        }

        /* =========================================================
   ORIGINE TECHNOPARTS
   WEB = ROSSO
   NOWEB = GRIGIO DARK
   ========================================================= */

.tp-origin-badge {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;

  min-width: 48px;

  padding: 4px 9px !important;

  border-radius: 999px !important;

  font-size: 0.6rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.04em;

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}


/* WEB */
.tp-origin-web {
  color: #ffffff !important;

  background:
    linear-gradient(
      135deg,
      #b81f25,
      #94191e
    ) !important;

  border:
    1px solid rgba(184, 31, 37, 0.35) !important;
}


/* NOWEB */
.tp-origin-noweb {
  color: #ffffff !important;

  background:
    linear-gradient(
      135deg,
      #495057,
      #343a40
    ) !important;

  border:
    1px solid rgba(52, 58, 64, 0.25) !important;
}

/* =========================================================
   ORIGINE TECHNOPARTS
   Rosso outline + rosso glass trasparente
   ========================================================= */

.tp-origin-badge {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;

  min-width: 50px;

  padding: 4px 9px !important;

  color: #b81f25 !important;

  background:
    linear-gradient(
      135deg,
      rgba(184, 31, 37, 0.16),
      rgba(184, 31, 37, 0.07)
    ) !important;

  border:
    1px solid rgba(184, 31, 37, 0.62) !important;

  border-radius: 999px !important;

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.30),
    0 3px 8px rgba(184,31,37,0.08) !important;

  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);

  font-size: 0.58rem !important;
  font-weight: 850 !important;
  letter-spacing: 0.04em;
}

.tp-dashboard-shell
.tp-dashboard-grid
.badge.tp-origin-badge {
  color: #b81f25 !important;

  background:
    linear-gradient(
      135deg,
      rgba(184, 31, 37, 0.16),
      rgba(184, 31, 37, 0.07)
    ) !important;

  border:
    1px solid rgba(184, 31, 37, 0.62) !important;
}


        /* =========================================================
           MODALE CLIENTE — LIGHT GREY LIQUID GLASS
           ========================================================= */

        .tp-client-modal-backdrop.show {
          opacity: 0.86 !important;
          background: #101419 !important;

          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }

        .tp-client-modal-content {
          color: #252b32 !important;

          background:
            radial-gradient(
              circle at 0% 0%,
              rgba(184, 31, 37, 0.12),
              transparent 34%
            ),
            radial-gradient(
              circle at 100% 8%,
              rgba(255,255,255,0.28),
              transparent 28%
            ),
            linear-gradient(
              145deg,
              rgba(214,219,224,0.97),
              rgba(188,194,200,0.95)
            ) !important;

          border:
            1px solid rgba(39,45,53,0.10) !important;

          box-shadow:
            0 32px 80px rgba(18,23,29,0.34),
            inset 0 1px 0 rgba(255,255,255,0.55) !important;

          backdrop-filter:
            blur(15px)
            saturate(112%) !important;

          -webkit-backdrop-filter:
            blur(15px)
            saturate(112%) !important;
        }

        .tp-client-modal-content::before {
          background:
            linear-gradient(
              90deg,
              #b81f25,
              transparent
            ) !important;
        }

        /* HEADER */

        .tp-client-modal-header {
          background:
            rgba(221,225,229,0.48) !important;

          border-bottom:
            1px solid rgba(39,45,53,0.07) !important;
        }

        .tp-client-modal-name {
          color: #252b32 !important;
        }

        .tp-client-modal-location {
          color: #68717b !important;
        }

        .tp-client-modal-location svg {
          color: #b81f25 !important;
        }

        .tp-client-modal-icon {
          color: #b81f25 !important;

          background:
            linear-gradient(
              145deg,
              rgba(184,31,37,0.14),
              rgba(184,31,37,0.055)
            ) !important;

          border:
            1px solid rgba(184,31,37,0.24) !important;

          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.38) !important;
        }

        .tp-client-modal-content .btn-close {
          background-color:
            rgba(44,51,59,0.08) !important;

          filter: none !important;
          opacity: 0.68 !important;
        }

        .tp-client-modal-content .btn-close:hover {
          background-color:
            rgba(184,31,37,0.10) !important;

          opacity: 1 !important;
        }

        /* BODY */

        .tp-client-modal-body {
          color: #303740 !important;

          background:
            transparent !important;

          overflow-y:
            auto !important;

          scrollbar-width:
            none !important;

          -ms-overflow-style:
            none !important;
        }

        .tp-client-modal-body::-webkit-scrollbar {
          display:
            none !important;

          width:
            0 !important;

          height:
            0 !important;
        }

        /* PILLS */

        .tp-modal-pill {
          color: #555e68 !important;

          background:
            rgba(224,228,232,0.50) !important;

          border:
            1px solid rgba(39,45,53,0.09) !important;

          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.42) !important;
        }

        /* Tipo documento vero: OW4 / OU4 / OWU4 ecc. */
        .tp-modal-document {
          color: #a81920 !important;

          background:
            linear-gradient(
              135deg,
              rgba(184,31,37,0.15),
              rgba(184,31,37,0.06)
            ) !important;

          border:
            1px solid rgba(184,31,37,0.52) !important;

          font-weight:
            850 !important;
        }

        .tp-modal-lm {
          color: #b81f25 !important;

          background:
            rgba(184,31,37,0.09) !important;

          border-color:
            rgba(184,31,37,0.20) !important;
        }

        /* KPI */

        .tp-client-modal-total > div {
          background:
            rgba(222,226,230,0.50) !important;

          border:
            1px solid rgba(39,45,53,0.075) !important;

          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.40) !important;
        }

        .tp-client-modal-total span {
          color: #727a84 !important;
        }

        .tp-client-modal-total strong {
          color: #282f37 !important;
        }

        /* DETAIL BOX */

        .tp-client-detail-box {
          background:
            rgba(200,205,211,0.42) !important;

          border:
            1px solid rgba(39,45,53,0.075) !important;
        }

        .tp-client-detail-heading {
          border-bottom:
            1px solid rgba(39,45,53,0.07) !important;
        }

        .tp-client-detail-heading div span {
          color: #747c86 !important;
        }

        .tp-client-detail-heading div strong {
          color: #343b43 !important;
        }

        .tp-client-detail-heading > span {
          color: #707883 !important;
        }

        /* SCROLL DETTAGLIO */

        .tp-client-detail-scroll {
          overflow-y:
            auto !important;

          scrollbar-width:
            none !important;

          -ms-overflow-style:
            none !important;
        }

        .tp-client-detail-scroll::-webkit-scrollbar {
          display:
            none !important;

          width:
            0 !important;

          height:
            0 !important;
        }

        /* TABELLA MODAL */

        .tp-modal-detail-table {
          color: #505862 !important;
          background: transparent !important;
        }

        .tp-modal-detail-table
        > :not(caption)
        > *
        > * {
          box-shadow: none !important;
        }

        .tp-modal-detail-table thead th {
          color: #666e78 !important;

          background:
            rgba(184,190,196,0.96) !important;

          border-bottom:
            1px solid rgba(39,45,53,0.09) !important;
        }

        .tp-modal-detail-table tbody td {
          color: #505862 !important;

          background:
            rgba(210,215,220,0.30) !important;

          border-bottom:
            1px solid rgba(39,45,53,0.065) !important;
        }

        .tp-modal-detail-table tbody tr:hover td {
          background:
            rgba(184,31,37,0.055) !important;
        }

        .tp-modal-brand strong {
          color: #292f36 !important;
        }

        .tp-modal-brand small {
          color: #747c86 !important;
        }

        .tp-modal-value {
          color: #b81f25 !important;
        }

        .tp-modal-detail-table tfoot td {
          color: #282f37 !important;

          background:
            rgba(184,31,37,0.085) !important;

          border-top:
            1px solid rgba(184,31,37,0.12) !important;
        }

        .tp-modal-detail-table tfoot td:last-child {
          color: #b81f25 !important;
        }

      `}</style>
    </>
  );
}

export default BasicExample;